"use client";

import { ArrowLeft, Check, Lock, Mail } from "lucide-react";
import { useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import type { BillingCurrency } from "../../../convex/lib/billingRail";
import { displayUsdCentsFromTtd, usdWholeDollarCents } from "../../../convex/lib/billingRail";
import { SanityMark } from "@/components/sanity-mark";
import { PaypalCardPreview, PaypalCheckoutButtons } from "@/studio/components/PaypalCheckoutButtons";
import { useMutation } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { CHECKOUT_METHODS } from "./paymentMethods";
import { CurrencyToggle } from "./CurrencyToggle";
import { useCheckoutReceiptEmails } from "./useCheckoutCurrency";
import "./unified-checkout.css";

export type CheckoutLine = {
  label: string;
  ttdCents: number;
  /** When set, USD display uses this instead of the FX conversion. */
  usdCents?: number;
  detail?: string;
};

/** Lazy PayPal: the order is created only on card submit or PayPal click. */
export type CheckoutPaypalSession = {
  clientId: string;
  createOrder: () => Promise<string>;
  onApprove: (orderId?: string) => void | Promise<void>;
  /** Buyer closed the PayPal popup / cancelled. Quiet: no toast. */
  onCancel?: () => void;
  /** Raw SDK error; the session decides the copy (popup closed is a cancel). */
  onError: (error: unknown) => void;
};

function formatCheckoutMoney(cents: number, currency: BillingCurrency): string {
  const negative = cents < 0;
  const amount = Math.abs(cents) / 100;
  const face = amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const body = currency === "TTD" ? `TT$${face}` : `US$${face}`;
  return negative ? `-${body}` : body;
}

function usdCentsFor(ttdCents: number, fxUsdToTtd: number | null, exact = false): number | null {
  if (!fxUsdToTtd || ttdCents === 0) return ttdCents === 0 ? 0 : null;
  const sign = ttdCents < 0 ? -1 : 1;
  // Charged totals round to whole dollars; line items (discounts) convert exactly so a small discount is not shown as US$1.
  const abs = Math.abs(ttdCents);
  return sign * (exact ? displayUsdCentsFromTtd(abs, fxUsdToTtd) : usdWholeDollarCents(abs, fxUsdToTtd));
}

function useDesktopSplit() {
  const [desktop, setDesktop] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 900px)");
    const sync = () => setDesktop(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);
  return desktop;
}

export function UnifiedCheckout({
  title,
  lines,
  totalTtdCents,
  /** Fixed USD price. Sanity plans do not convert from TTD. */
  fixedUsdCents,
  totalLabel = "Total due today",
  hideSubtotal = false,
  /** Product name in the subscribe heading. */
  brand = "Studio",
  wordmark = "Yatishara Studio",
  legalLine = "Powered by Studio",
  methodDetail,
  fxUsdToTtd = null,
  fxApproximate = false,
  currency,
  onCurrencyChange,
  busy = false,
  disabled = false,
  status,
  onPay,
  paypal = null,
  preview = false,
  footer,
  beforePay,
  receiptsSummary = false,
  email = "",
  intent = "pay",
  period,
  taxLabel = "Included",
  onBack,
  backLabel = "Change plan",
  picker,
  viewport = false,
  ttdAllowed = true,
  gradient = "soft",
  receiptEmails,
  currencyOrder,
}: {
  title: string;
  lines: CheckoutLine[];
  totalTtdCents: number;
  fixedUsdCents?: number;
  /** Label for the last totals row (default "Total due today"). */
  totalLabel?: string;
  /** Hide the Subtotal row (when the lines already show Total and a discount). */
  hideSubtotal?: boolean;
  brand?: string;
  /** Text next to the logo (default "Yatishara Studio"). */
  wordmark?: string;
  /** Footer line before Terms/Privacy. */
  legalLine?: string;
  /** Override a payment row subtitle per currency (e.g. Ads). */
  methodDetail?: Partial<Record<"TTD" | "USD", string>>;
  fxUsdToTtd?: number | null;
  /** True when the US$ figure is the display stand-in, not the live cache. */
  fxApproximate?: boolean;
  currency: BillingCurrency;
  onCurrencyChange: (currency: BillingCurrency) => void;
  busy?: boolean;
  disabled?: boolean;
  status?: string;
  onPay: () => void;
  paypal?: CheckoutPaypalSession | null;
  /** Static PayPal card. Does not load the PayPal script. */
  preview?: boolean;
  footer?: ReactNode;
  /** Rendered in the payment column just above Pay (e.g. a terms checkbox). */
  beforePay?: ReactNode;
  /** Show one line (personal email) instead of receipt choices; receipts still go to every address. */
  receiptsSummary?: boolean;
  email?: string;
  intent?: "subscribe" | "pay";
  period?: string;
  taxLabel?: string;
  onBack?: () => void;
  /** Back pill copy. Plan checkouts keep "Change plan"; top-up passes "Change amount". */
  backLabel?: string;
  /** Plan cards. The summary follows the selected plan. */
  picker?: ReactNode;
  /** Desktop: cover the viewport. Mobile stays a stacked card. */
  viewport?: boolean;
  /** Trinidad and Tobago only: TTD toggle and Wam. Elsewhere USD + PayPal only. */
  ttdAllowed?: boolean;
  /** Left column accent gradient: soft 20/12/8% (sign-up, light page), strong 34/20/12% (dark app panel). */
  gradient?: "soft" | "strong";
  /** When set, the only receipt addresses offered (replaces the signed-in account list). */
  receiptEmails?: string[];
  /** Currency button order (default USD, TTD). */
  currencyOrder?: readonly ("USD" | "TTD")[];
}) {
  const desktop = useDesktopSplit();
  const liveEmails = useCheckoutReceiptEmails(preview);
  const receiptOptions = receiptEmails
    ? Array.from(new Set(receiptEmails.filter(Boolean)))
    : preview
    ? Array.from(new Set([email || "jose@example.com", "jose.example@yatishara.com"]))
    : liveEmails.length > 0
      ? liveEmails
      : email
        ? [email]
        : [];
  const [receiptOff, setReceiptOff] = useState<string[]>([]);
  const receiptPicked = receiptOptions.filter((e) => !receiptOff.includes(e));
  const saveReceipts = useMutation(api.users.setReceiptEmails);
  const receiptKey = receiptPicked.join(",");
  useEffect(() => {
    if (preview || !receiptKey) return;
    void saveReceipts({ emails: receiptKey.split(",") }).catch(() => {});
  }, [preview, receiptKey, saveReceipts]);
  const toggleReceipt = (addr: string) => {
    const on = !receiptOff.includes(addr);
    if (on && receiptPicked.length <= 1) return;
    setReceiptOff((prev) => (on ? [...prev, addr] : prev.filter((x) => x !== addr)));
  };
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const usdTotal = fixedUsdCents != null ? fixedUsdCents : usdCentsFor(totalTtdCents, fxUsdToTtd);
  const money = (ttdCents: number, exact = false, lineUsd?: number) => {
    if (currency === "TTD") return formatCheckoutMoney(ttdCents, "TTD");
    const usd = lineUsd != null ? lineUsd : fixedUsdCents != null && ttdCents === totalTtdCents ? fixedUsdCents : usdCentsFor(ttdCents, fxUsdToTtd, exact);
    if (usd == null) return "Rate loading";
    const face = formatCheckoutMoney(usd, "USD");
    return lineUsd != null || fixedUsdCents != null ? face : fxApproximate ? `approx. ${face}` : face;
  };
  const payLabel = intent === "subscribe" ? "Subscribe" : "Pay";
  const showPaypalButtons = currency === "USD" && paypal;
  const cardPayLabel =
    usdTotal == null
      ? payLabel
      : `${payLabel} US$${(usdTotal / 100).toLocaleString("en-US", {
          minimumFractionDigits: usdTotal % 100 === 0 ? 0 : 2,
          maximumFractionDigits: 2,
        })}`;
  const heading = intent === "subscribe" ? `Subscribe to ${brand} ${title}` : title;
  const rateNote = fxUsdToTtd == null ? "" : `1 US$ = ${Number.isInteger(fxUsdToTtd) ? fxUsdToTtd : fxUsdToTtd.toFixed(2)} TT$`;
  const usdPill =
    usdTotal == null
      ? "Rate loading"
      : `${fxApproximate ? "approx. " : ""}${formatCheckoutMoney(usdTotal, "USD")}`;

  const tree = (
    <section
      className={`unified-checkout uc2${viewport && desktop ? " is-viewport" : ""}${gradient === "strong" ? " is-gradient-strong" : ""}`}
      aria-label="Checkout"
    >
      <div className="uc2-split">
        <div className="uc2-left">
          <div className="uc2-col">
            <div className="uc2-brand">
              <span className="uc2-logo-mark"><SanityMark height={22} className="uc2-sanity-mark" title="Sanity" /><span className={wordmark ? "uc2-wordmark uc2-wordmark-own" : "uc2-wordmark"}>{wordmark ?? "Yatishara Studio"}</span></span>
              {onBack ? (
                <button type="button" className="uc2-back-link" onClick={onBack}>
                  <ArrowLeft size={14} strokeWidth={2.25} aria-hidden="true" />
                  <span>{backLabel}</span>
                </button>
              ) : null}
            </div>
            {picker ? <div className="unified-checkout-picker">{picker}</div> : null}
            <p className="uc2-heading">{heading}</p>
            <p className="uc2-price">
              <strong>{money(totalTtdCents)}</strong>
              {period ? <span>{period}</span> : null}
            </p>
            {ttdAllowed ? (
            <div className="uc2-currency-row">
            <CurrencyToggle currency={currency} onChange={onCurrencyChange} order={currencyOrder} />
              {rateNote ? <span className="uc2-rate">{rateNote}</span> : null}
            </div>
            ) : (
              <div className="uc2-currency-spacer" aria-hidden="true" />
            )}
            <div className="uc2-items">
              {lines.map((line) => (
                <div className="uc2-item" key={line.label}>
                  <span className="uc2-item-copy">
                    <strong>{line.label}</strong>
                    {line.detail ? <span>{line.detail}</span> : null}
                  </span>
                  <span className="uc2-item-price">{money(line.ttdCents, line.ttdCents < 0, line.usdCents)}</span>
                </div>
              ))}
            </div>
            <dl className="uc2-totals">
              {hideSubtotal ? null : (
              <div>
                <dt>Subtotal</dt>
                <dd>{money(totalTtdCents)}</dd>
              </div>
              )}
              <div>
                <dt>Tax</dt>
                <dd>{taxLabel}</dd>
              </div>
              <div className="is-total">
                <dt>{totalLabel}</dt>
                <dd>{money(totalTtdCents)}</dd>
              </div>
            </dl>
          </div>
          {footer ? <div className="uc2-skip-left"><div className="uc2-skip-left-inner">{footer}</div></div> : null}
        </div>
        <div className="uc2-right">
          <div className="uc2-col">
            {receiptsSummary ? (
              receiptOptions.length > 0 ? (
                <div className="uc2-receipt-card">
                  <span className="uc2-receipt-icon" aria-hidden="true"><Mail size={16} strokeWidth={2} /></span>
                  <span className="uc2-receipt-copy">
                    <span className="uc2-receipt-k">Receipt sent to</span>
                    <strong className="uc2-receipt-v" title={receiptOptions[0]}>{receiptOptions[0]}</strong>
                  </span>
                </div>
              ) : null
            ) : (<>
            <p className="uc2-label" id="uc2-receipts-label">Send receipts to</p>
            <div className="uc2-methods uc2-receipts" role="group" aria-labelledby="uc2-receipts-label">
              {receiptOptions.map((addr) => {
                const on = receiptPicked.includes(addr);
                return (
                  <button
                    key={addr}
                    type="button"
                    role="checkbox"
                    aria-checked={on}
                    data-no-press=""
                    className={`uc2-method uc2-receipt${on ? " is-selected" : ""}`}
                    onClick={() => toggleReceipt(addr)}
                  >
                    <span className="uc2-check" aria-hidden="true">
                      {on ? <Check size={12} strokeWidth={3} /> : null}
                    </span>
                    <span className="uc2-method-copy">
                      <strong>{addr}</strong>
                      <span>{addr.endsWith("@yatishara.com") ? "Your Yatishara address" : "Your personal email"}</span>
                    </span>
                  </button>
                );
              })}
            </div>
            </>)}
            <p className="uc2-label">Payment method</p>
            <div className="uc2-methods" role="radiogroup" aria-label="Payment method">
              {CHECKOUT_METHODS.filter((method) => ttdAllowed || method.currency === "USD").map((method) => {
                const on = method.currency === currency;
                return (
                  <button
                    key={method.id}
                    type="button"
                    role="radio"
                    aria-checked={on}
                    data-no-press=""
                    className={`uc2-method${on ? " is-selected" : ""}`}
                    onClick={() => onCurrencyChange(method.currency)}
                  >
                    <span className="uc2-radio" aria-hidden="true" />
                    <span className={`uc2-logo is-${method.chip}`}>
                      <img src={method.logoSrc} alt={method.logoAlt} height={18} />
                    </span>
                    <span className="uc2-method-copy">
                      <strong>{method.label}</strong>
                      <span>{methodDetail?.[method.currency as "TTD" | "USD"] ?? method.detail}</span>
                    </span>
                  </button>
                );
              })}
            </div>
            {beforePay ? <div className="uc2-before-pay">{beforePay}</div> : null}
            {currency === "USD" && preview && !paypal ? (
              <PaypalCardPreview payLabel={cardPayLabel} onPay={onPay} />
            ) : showPaypalButtons ? (
              <PaypalCheckoutButtons
                clientId={paypal.clientId}
                createOrder={paypal.createOrder}
                payLabel={cardPayLabel}
                onApprove={paypal.onApprove}
                onCancel={paypal.onCancel}
                onError={paypal.onError}
              />
            ) : (
              <button
                type="button"
                className="uc2-pay"
                disabled={disabled || busy}
                aria-busy={busy}
                onClick={onPay}
              >
                {busy ? "Please wait" : payLabel}
              </button>
            )}
            <p className="uc2-secure">
              <Lock size={12} strokeWidth={2.25} aria-hidden="true" />
              <span>Secure checkout</span>
            </p>
            {status ? <p className="uc2-status">{status}</p> : null}
            <p className="uc2-legal">
              <span>{legalLine}</span>
              <span aria-hidden="true">|</span>
              <a href="/legal/terms/">Terms</a>
              <a href="/legal/privacy/">Privacy</a>
            </p>
          </div>
        </div>
      </div>
    </section>
  );

  // Viewport checkouts wait for the first client pass (mounted and desktop settle in the same
  // commit) so the tree is placed once. Flipping from inline to a body portal after mount
  // remounted PaypalCheckoutButtons mid SDK load, which reloaded the SDK and closed the
  // first PayPal popup on the sign-up page.
  if (viewport && !mounted) return null;
  if (viewport && desktop) return createPortal(tree, document.body);
  return tree;
}
